import { Divider, Field, Flex, InputLabel, TextButton } from '@procurify/ui'
import { useSelector } from 'react-redux'

import { DepartmentSelect } from '@webapp/components/DepartmentSelect'
import { type FilterManagerContextValues } from '@webapp/components/FilterManager/FilterManagerContext'
import { FiltersPopover } from '@webapp/components/FiltersPopover'
import { SavedFilterManager } from '@webapp/components/SavedFilterManager'
import { UserSelect } from '@webapp/components/UserSelect'
import { RequestTypes } from '@webapp/core/entities/internal/enums/RequestTypeEnum'
import { makeSelectFeatureSwitches } from '@webapp/core/stores/global'
import { useProcurifyIntl } from '@webapp/hooks'
import { type IBudgetCategoryListViewFilters } from '@webapp/hooks/useBudgetCategoryListFilterManager'
import { type SavedFilterCollectionKeys } from '@webapp/hooks/useSavedFilterManager/types'
import { BudgetCategoryTypes } from '@webapp/models/enums/BudgetCategoryType'
import { Permissions } from '@webapp/platform/iam'
import { BudgetCategoryListSavedFilterManager } from './components/BudgetCategoryListSavedFilterManager'
import messages from './messages'
import { AccountCodeSelect } from '../../AccountCodeSelect'
import { BudgetCategoryTypeSelect } from '../BudgetCategoryTypeSelect'
import { type BudgetCategoryTypeOption } from '../BudgetCategoryTypeSelect/BudgetCategoryTypeSelect'
import { useBudgetCategoryTypeOptions } from '../BudgetCategoryTypeSelect/hooks/useBudgetCategoryTypeOptions'

export interface IBudgetCategoriesViewFilterButtonProps {
  collectionId: keyof Pick<
    SavedFilterCollectionKeys,
    'settings.manage.budget.category' | 'dashboard.budget.category.list'
  >
  filterManager: FilterManagerContextValues<IBudgetCategoryListViewFilters>
  shouldHideDepartmentOptionsForRequesters?: boolean
  onFilterChange?: () => undefined | void
}

export const BudgetCategoryListFilterButton = ({
  collectionId,
  filterManager,
  shouldHideDepartmentOptionsForRequesters = false,
  onFilterChange = () => undefined,
}: IBudgetCategoriesViewFilterButtonProps) => {
  const intl = useProcurifyIntl()
  const { enable_flexible_spend_tracking: isFlexibleSpendTrackingEnabled } =
    useSelector(makeSelectFeatureSwitches())

  const budgetCategoryTypeOptions = useBudgetCategoryTypeOptions()

  const { filterValues, clearFilters, addFilter, removeFilter } = filterManager

  const USER_SELECT_PERMISSIONS_FILTER = [
    Permissions.APPROVE_EXPENSEREPORT,
    Permissions.APPROVE_ORDER,
    Permissions.APPROVE_PAY_REQUEST,
  ]

  const onBudgetCategoryTypeChange = (option: BudgetCategoryTypeOption) => {
    const paramValue = option.value

    if (paramValue) {
      addFilter({
        key: 'source_of_truth',
        paramValue,
        value: option,
      })
    } else {
      removeFilter({
        key: 'source_of_truth',
        paramValue: filterValues.source_of_truth?.value,
        value: filterValues.source_of_truth,
      })
    }

    onFilterChange()
  }

  const budgetTypeLabel = intl.formatMessage(
    isFlexibleSpendTrackingEnabled
      ? messages.budgetType
      : messages.budgetCategoryType
  )

  return (
    <BudgetCategoryListSavedFilterManager
      collectionId={collectionId}
      budgetCategoryTypeOptions={budgetCategoryTypeOptions}
      filterManager={filterManager}
    >
      <FiltersPopover>
        {({ dismiss }) => (
          <>
            <Field>
              <InputLabel htmlFor='saved-filter'>
                {intl.formatMessage(messages.saveFilterButtonLabel)}
              </InputLabel>
              <SavedFilterManager.Select
                inputId='saved-filter'
                onEdit={dismiss}
              />
            </Field>

            <Divider />

            <Flex.Row flexDirection='column'>
              {collectionId !== 'dashboard.budget.category.list' && (
                <Flex.Item>
                  <Field>
                    <InputLabel htmlFor='viewing-access-filter'>
                      {intl.formatMessage(messages.viewingAccess)}
                    </InputLabel>
                    <UserSelect
                      inputId='viewing-access-filter'
                      name={intl.formatMessage(messages.viewingAccess)}
                      defaultOptions={false}
                      permissions={USER_SELECT_PERMISSIONS_FILTER}
                      value={filterValues.users}
                      onChange={(value) => {
                        addFilter({
                          key: 'users',
                          paramValue: value.id,
                          value,
                        })
                        onFilterChange()
                      }}
                    />
                  </Field>
                </Flex.Item>
              )}

              <Flex.Item>
                <Field>
                  <InputLabel htmlFor='departments-filter'>
                    {intl.formatMessage(messages.departments)}
                  </InputLabel>
                  <DepartmentSelect
                    inputId='departments-filter'
                    name={intl.formatMessage(messages.departments)}
                    value={filterValues.departments}
                    getOptionLabel={(department) =>
                      `${department?.branch?.name} - ${department.name}`
                    }
                    params={
                      !shouldHideDepartmentOptionsForRequesters
                        ? { requestable: RequestTypes.ORDER }
                        : null
                    }
                    onChange={(value) => {
                      addFilter({
                        key: 'departments',
                        paramValue: value.id,
                        value,
                      })
                      onFilterChange()
                    }}
                  />
                </Field>
              </Flex.Item>

              <Flex.Item>
                <Field>
                  <InputLabel htmlFor='account-code-filter'>
                    {intl.formatMessage(messages.accountCodes)}
                  </InputLabel>
                  <AccountCodeSelect
                    inputId='account-code-filter'
                    name={intl.formatMessage(messages.accountCodes)}
                    defaultOptions={false}
                    value={filterValues.account_codes}
                    getOptionLabel={(accCode) =>
                      `${accCode.code} - ${accCode.description}`
                    }
                    onChange={(value) => {
                      addFilter({
                        key: 'account_codes',
                        paramValue: value.id,
                        value,
                      })
                      onFilterChange()
                    }}
                  />
                </Field>
              </Flex.Item>

              {collectionId !== 'dashboard.budget.category.list' && (
                <Flex.Item>
                  <Field>
                    <InputLabel htmlFor='budget-category-type-filter'>
                      {budgetTypeLabel}
                    </InputLabel>
                    <BudgetCategoryTypeSelect
                      name={budgetTypeLabel}
                      inputId='budget-category-type-filter'
                      onChange={(value) => onBudgetCategoryTypeChange(value)}
                      value={filterValues.source_of_truth}
                      defaultParam={BudgetCategoryTypes.ALL}
                    />
                  </Field>
                </Flex.Item>
              )}
            </Flex.Row>

            <Divider />

            <Flex.Row>
              <Flex.Item>
                <SavedFilterManager.SaveButton onClick={dismiss} />
              </Flex.Item>
              <Flex.Item ml='auto'>
                <TextButton
                  onClick={() => {
                    clearFilters()
                    dismiss()
                  }}
                >
                  {intl.formatMessage(messages.clearFilterButtonLabel)}
                </TextButton>
              </Flex.Item>
            </Flex.Row>
          </>
        )}
      </FiltersPopover>
    </BudgetCategoryListSavedFilterManager>
  )
}
